import { TransactionDrawer, SubinventoryQtyBox, TransactionItemsList } from '../../../atoms';
import {
  usePlants,
  useInventoryItem,
  useSubinventoryCodes,
  useSubinventoryInfo,
  useTransactionItems,
  useCreateInvMgmtTransaction,
  useUpdateRequest,
  useLocators,
} from '../../../../hooks';
import { Select, toast } from '@pella/sash-framework';
import { useState } from 'react';
import * as classes from './DirectOrgTransfer.module.css';
import { STATUSES, TRANSACTION_TYPES } from '../../../../data/constants';
import { adjustSubinventoryInfo, formatTransactionsForAPI } from '../../../../data/functions';

/**
 * Drawer for the Direct Organization Transfer transaction.
 * @param {Local.ParentTransactionDrawerProps} props - Component props
 */
export const DirectOrgTransferDrawer = ({ open, close, request }) => {
  const [fromSubinventory, setFromSubinventory] = useState('');
  const [toSubinventory, setToSubinventory] = useState('');
  const [selectedLocator, setSelectedLocator] = useState('');

  const { mutate: createInvMgmtTransaction, isPending } = useCreateInvMgmtTransaction();
  const { mutate: updateRequest } = useUpdateRequest();

  const { data: plants } = usePlants();

  const queuePlant = plants?.find((p) => p.id === request?.action?.queue?.plantId);
  const queueOrgId = queuePlant?.organizationId ?? 0;

  const actionPlant = plants?.find((p) => p.id === request?.action?.location?.plantId);
  const actionOrgId = actionPlant?.organizationId ?? 0;

  const inputValue = request?.dynamicInput ?? request?.dropDownListVal1 ?? request?.dropDownListVal2 ?? '';

  const { data: inventoryItem } = useInventoryItem({
    item: inputValue,
    organizationId: queueOrgId,
  });

  const { transactionItems, addQuantity, addFullLot, deleteItem } = useTransactionItems();

  const inventoryItemId = inventoryItem?.[0]?.inventoryItemId;

  const { data: queueSubinventoryCodes } = useSubinventoryCodes({
    organizationId: queueOrgId,
    inventoryItemId: inventoryItemId ?? 0,
    qoh: 'Y',
  });

  const uniqueFromSubinventoryCodes = [...new Set(queueSubinventoryCodes?.map((c) => c.subinventoryCode))];

  const { data: actionSubinventoryCodes } = useSubinventoryCodes({
    organizationId: actionOrgId,
    inventoryItemId: inventoryItemId ?? 0,
    qoh: 'N',
  });

  const uniqueToSubinventoryCodes = [...new Set(actionSubinventoryCodes?.map((c) => c.subinventoryCode))];

  const { data: subinventoryInfo } = useSubinventoryInfo({
    organizationId: queueOrgId,
    inventoryItemId: inventoryItemId ?? 0,
    subinventoryCode: fromSubinventory,
  });

  const { data: locators } = useLocators({
    organizationId: actionOrgId,
    inventoryItemId: inventoryItemId ?? 0,
    subinventoryCode: toSubinventory,
  });

  const adjustedSubinventoryInfo = adjustSubinventoryInfo(subinventoryInfo || [], transactionItems);

  /** Resets form state and closes the drawer */
  const handleClose = () => {
    close();
  };

  /**
   * Updates the request to Completed status and closes the drawer.
   * @param {(request: Pella.ProductionRequestAPI.Request, onSettled: () => void) => void} completeWithLinkedActionCheck
   * @param {string} errorMessage - Toast message shown if the status update fails.
   */
  const handleCompleteRequest = (completeWithLinkedActionCheck, errorMessage) => {
    completeWithLinkedActionCheck(request, () =>
      updateRequest(
        { id: request.id, status: STATUSES.COMPLETED },
        {
          onSuccess: () => {
            toast.success('Request completed successfully!');
            handleClose();
          },
          onError: () => {
            toast.error(errorMessage);
          },
        },
      ),
    );
  };

  /**
   * Handles form submission
   * @param {(request: Pella.ProductionRequestAPI.Request, onSettled: () => void) => void} completeWithLinkedActionCheck
   */
  const handleSubmit = (completeWithLinkedActionCheck) => {
    const formattedTransactions = formatTransactionsForAPI(
      transactionItems.map((item) => ({
        ...item,
        transferSubinventory: toSubinventory,
        transferOrganization: actionOrgId,
        transferLocatorId: selectedLocator === '' ? null : Number(selectedLocator),
      })),
    );

    createInvMgmtTransaction(
      {
        orgId: queueOrgId,
        transactions: formattedTransactions,
      },
      {
        onSuccess: () =>
          handleCompleteRequest(
            completeWithLinkedActionCheck,
            'Request completed in WMS but failed to update status. Please refresh.',
          ),
        onError: () => toast.error('Failed to create inventory management transaction. Please try again.'),
      },
    );
  };

  const disableComplete = transactionItems.length === 0 || isPending;

  const disableAddQuantity = !fromSubinventory || !toSubinventory || (!locators?.length ? false : !selectedLocator);
  const disableAddFullLot = !fromSubinventory || !toSubinventory || (!locators?.length ? false : !selectedLocator);

  /**
   * Handles changes to the "To Subinventory" select input.
   * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event.
   */
  const handleToSubinventoryChange = (e) => {
    setToSubinventory(e.target.value);
    setSelectedLocator('');
  };

  return (
    <TransactionDrawer
      disableSubmit={disableComplete}
      open={open}
      close={handleClose}
      request={request}
      onComplete={handleSubmit}
    >
      {inventoryItem && inventoryItem.length > 0 && (
        <div>
          Selected Org: {actionOrgId} <br />
          Inventory Item ID: {inventoryItem[0]?.inventoryItemId} <br />
          Requested Item: {inventoryItem[0]?.item} <br />
          Description: {inventoryItem[0]?.description}
        </div>
      )}
      <div className={classes.fieldGrid}>
        <div className={classes.selectWrapper}>
          <Select
            className={classes.selectItem}
            labelText='From Sub Inventory'
            id='FromSubinventorySelect'
            required
            value={fromSubinventory}
            onChange={(e) => setFromSubinventory(e.target.value)}
            data-testid='from-subinventory-select'
          >
            <option value='' key=''>
              Select a Subinventory
            </option>
            {uniqueFromSubinventoryCodes?.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </div>

        <div className={classes.selectWrapper}>
          <Select
            className={classes.selectItem}
            labelText='To Sub Inventory'
            id='ToSubinventorySelect'
            required
            value={toSubinventory}
            onChange={handleToSubinventoryChange}
            data-testid='to-subinventory-select'
          >
            <option value='' key=''>
              Select a Subinventory
            </option>
            {uniqueToSubinventoryCodes?.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </div>

        {locators && locators.length > 0 && (
          <div className={classes.selectWrapper}>
            <Select
              className={classes.selectItem}
              labelText='Location To'
              id='LocatorToSelect'
              required
              value={selectedLocator}
              onChange={(e) => setSelectedLocator(e.target.value)}
              data-testid='locator-to-select'
            >
              <option value='' key=''>
                Select a Location
              </option>
              {locators.map((value) => (
                <option key={value.locatorId} value={value.locatorId}>
                  {value.locator}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>
      {subinventoryInfo && (
        <SubinventoryQtyBox
          subinventoryQtyInfo={adjustedSubinventoryInfo}
          onAddQuantity={addQuantity}
          onAddFullLot={addFullLot}
          transactionType={TRANSACTION_TYPES.DIRECT_ORG_TRANSFER}
          orgId={queueOrgId}
          disableAddFullLot={disableAddFullLot}
          disableAddQuantity={disableAddQuantity}
        />
      )}
      <TransactionItemsList items={transactionItems} onDelete={deleteItem} />
    </TransactionDrawer>
  );
};
